import React, { useEffect, useState, useCallback } from 'react';
import { X, AlertTriangle, ChevronLeft, ChevronRight, Loader2, RefreshCw, Search, Shield } from 'lucide-react';
import { AllertOrariDifformiService } from '../services/apiService';
import { AllertOrariDifformi, AllertDifformitaTipo, SpringPage } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { formatMinutiBreve } from '../utils/incaricoUtils';

// Filtro per tipo di lavoro.
type TipoFilter = AllertDifformitaTipo | 'TUTTI';

interface Props {
    onClose: () => void;
}

// YYYY-MM-DD in ora locale, non UTC.
const toIsoDate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const formatDataPianificata = (iso: string): string => {
    // iso = YYYY-MM-DD → render DD/MM/YYYY
    if (!iso || iso.length < 10) return iso || '—';
    const [y, m, d] = iso.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
};

// "2026-06-02T08:00:00" → "02/06 08:00"
const formatDateTimeShort = (iso?: string | null): string => {
    if (!iso) return '—';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!m) return iso;
    return `${m[3]}/${m[2]} ${m[4]}:${m[5]}`;
};

// Storico delle difformità orarie, filtrabile per data pianificata
// (default ultimi 7 giorni).
export const AllertOrariDifformiModal: React.FC<Props> = ({ onClose }) => {
    const { currentSedeId } = useAuth();
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    const [from, setFrom] = useState(toIsoDate(sevenDaysAgo));
    const [to, setTo] = useState(toIsoDate(today));
    // Filtro tipo: Tutti / Investigazioni / Servizi Fiduciari.
    const [tipo, setTipo] = useState<TipoFilter>('TUTTI');
    const [page, setPage] = useState(0);
    const [size] = useState(20);
    const [data, setData] = useState<SpringPage<AllertOrariDifformi> | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [backfilling, setBackfilling] = useState(false);
    const [backfillMsg, setBackfillMsg] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Filtro sulla sede del mandato, salvo titolare con 'ALL'.
            const sedeIdFilter = typeof currentSedeId === 'number' ? currentSedeId : undefined;
            const res = await AllertOrariDifformiService.search(from, to, page, size, sedeIdFilter, tipo);
            setData(res.data || null);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Errore nel caricamento');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [from, to, page, size, currentSedeId, tipo]);

    useEffect(() => { load(); }, [load]);

    const onChangeFrom = (v: string) => { setFrom(v); setPage(0); };
    const onChangeTo = (v: string) => { setTo(v); setPage(0); };

    const runBackfill = async () => {
        if (backfilling) return;
        if (!window.confirm("Ricalcolare lo storico difformità per tutti gli incarichi e turni? Operazione idempotente, può essere ripetuta.")) return;
        setBackfilling(true);
        setBackfillMsg(null);
        try {
            const res = await AllertOrariDifformiService.backfill();
            setBackfillMsg(res.message || 'Backfill completato');
            await load();
        } catch (e: any) {
            setBackfillMsg(e?.response?.data?.message || 'Errore backfill');
        } finally {
            setBackfilling(false);
        }
    };

    const totalElements = data?.totalElements ?? 0;
    const totalPages = data?.totalPages ?? 0;
    const rows = data?.content ?? [];

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-3xl lg:max-w-5xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center p-5 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                        <AlertTriangle size={20} className="text-rose-600" />
                        <h3 className="text-lg font-bold text-slate-800">Allert orari difformi</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={runBackfill}
                            disabled={backfilling}
                            title="Ricalcola lo storico per gli incarichi precedenti all'attivazione dell'allerta (si può ripetere)"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-colors disabled:opacity-50"
                        >
                            {backfilling ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                            Ricalcola storico
                        </button>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-slate-700 p-1 rounded-full hover:bg-slate-100 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                {backfillMsg && (
                    <div className="px-5 py-2 text-xs text-slate-600 bg-amber-50 border-b border-amber-100">{backfillMsg}</div>
                )}

                {/* Filtri */}
                <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-end flex-wrap gap-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Da</label>
                        <input
                            type="date"
                            value={from}
                            onChange={e => onChangeFrom(e.target.value)}
                            className="text-sm border border-slate-300 rounded-lg px-3 py-1.5"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">A</label>
                        <input
                            type="date"
                            value={to}
                            onChange={e => onChangeTo(e.target.value)}
                            className="text-sm border border-slate-300 rounded-lg px-3 py-1.5"
                        />
                    </div>

                    {/* Filtro tipo, sempre visibile anche senza turni. */}
                    <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Tipo</label>
                        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                            {([
                                { id: 'TUTTI' as TipoFilter, label: 'Tutti', icon: null },
                                { id: 'INCARICO' as TipoFilter, label: 'Investigazioni', icon: <Search size={12} /> },
                                { id: 'TURNO' as TipoFilter, label: 'Servizi Fiduciari', icon: <Shield size={12} /> },
                            ]).map(opt => {
                                const active = tipo === opt.id;
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => { setTipo(opt.id); setPage(0); }}
                                        className={
                                            'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-colors ' +
                                            (active
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-white text-slate-600 hover:bg-slate-50')
                                        }
                                    >
                                        {opt.icon}
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="ml-auto text-xs text-slate-500">
                        {loading ? (
                            <span className="inline-flex items-center gap-1.5">
                                <Loader2 size={12} className="animate-spin" /> Caricamento…
                            </span>
                        ) : (
                            <span><span className="font-bold text-slate-700">{totalElements}</span> {totalElements === 1 ? 'difformità' : 'difformità'} nel periodo</span>
                        )}
                    </div>
                </div>

                {/* Tabella */}
                <div className="max-h-[60vh] overflow-y-auto">
                    {error && (
                        <div className="p-5 text-sm text-rose-700 bg-rose-50 border-b border-rose-100">{error}</div>
                    )}
                    {!error && !loading && rows.length === 0 && (
                        <div className="p-10 text-center text-sm text-slate-400">
                            Nessuna difformità rilevata nel periodo selezionato.
                        </div>
                    )}
                    {rows.length > 0 && (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0">
                                <tr>
                                    <th rowSpan={2} className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200">Tipo</th>
                                    <th rowSpan={2} className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200">Data pianif.</th>
                                    <th rowSpan={2} className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200">Mandato / Servizio</th>
                                    <th rowSpan={2} className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200">Collaboratore</th>
                                    <th colSpan={2} className="px-3 py-1.5 text-center text-[11px] font-bold text-slate-500 uppercase border-l border-slate-200">Inizio</th>
                                    <th colSpan={2} className="px-3 py-1.5 text-center text-[11px] font-bold text-slate-500 uppercase border-l border-slate-200">Fine</th>
                                    <th rowSpan={2} className="px-3 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200 border-l">Scost.</th>
                                    <th rowSpan={2} className="px-3 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200">Soglia</th>
                                </tr>
                                <tr>
                                    <th className="px-3 py-1 text-left text-[10px] font-semibold text-slate-400 uppercase border-l border-b border-slate-200">Previsto</th>
                                    <th className="px-3 py-1 text-left text-[10px] font-semibold text-slate-400 uppercase border-b border-slate-200">Reale</th>
                                    <th className="px-3 py-1 text-left text-[10px] font-semibold text-slate-400 uppercase border-l border-b border-slate-200">Previsto</th>
                                    <th className="px-3 py-1 text-left text-[10px] font-semibold text-slate-400 uppercase border-b border-slate-200">Reale</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map(r => {
                                    const isTurno = r.tipo === 'TURNO';
                                    return (
                                    <tr key={r.id} className="hover:bg-slate-50">
                                        <td className="px-3 py-2.5">
                                            <span className={
                                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ' +
                                                (isTurno
                                                    ? 'bg-emerald-100 text-emerald-700'
                                                    : 'bg-indigo-100 text-indigo-700')
                                            }>
                                                {isTurno ? <Shield size={10} /> : <Search size={10} />}
                                                {isTurno ? 'Servizio' : 'Indagine'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 font-mono text-slate-700">{formatDataPianificata(r.dataPianificata)}</td>
                                        <td className="px-3 py-2.5 text-slate-700">{r.mandatoNome || '—'}</td>
                                        <td className="px-3 py-2.5 text-slate-700">{r.collaboratoreNome || '—'}</td>
                                        <td className="px-3 py-2.5 font-mono text-[12px] text-slate-600 border-l border-slate-100">{formatDateTimeShort(r.inizioPianificato)}</td>
                                        <td className="px-3 py-2.5 font-mono text-[12px] text-slate-900 font-semibold">{formatDateTimeShort(r.inizioReale)}</td>
                                        <td className="px-3 py-2.5 font-mono text-[12px] text-slate-600 border-l border-slate-100">{formatDateTimeShort(r.finePianificata)}</td>
                                        <td className="px-3 py-2.5 font-mono text-[12px] text-slate-900 font-semibold">{formatDateTimeShort(r.fineReale)}</td>
                                        <td className="px-3 py-2.5 text-right border-l border-slate-100">
                                            <div className="font-bold text-rose-700">+{formatMinutiBreve(r.scostamentoInizioMin)}</div>
                                            <div className="text-[11px] font-bold text-rose-700/80">{r.scostamentoFineMin != null ? `+${formatMinutiBreve(r.scostamentoFineMin)}` : '—'}</div>
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-slate-500">{formatMinutiBreve(r.sogliaMin)}</td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Paginazione */}
                {totalPages > 1 && (
                    <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-sm">
                        <span className="text-slate-500">
                            Pagina <span className="font-bold text-slate-700">{(data?.number ?? 0) + 1}</span> di {totalPages}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={page === 0 || loading}
                                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={page >= totalPages - 1 || loading}
                                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AllertOrariDifformiModal;
